import { createTheme } from '@mui/material/styles';
import { getDesignTokens, colors, gradients, shadows, borderRadius, neu, brand, food } from './palette';
import { typography, displayFont } from './typography';

export const createAppTheme = (mode: 'light' | 'dark') => {
  const isDark = mode === 'dark';
  const n = isDark ? neu.dark : neu.light;

  // Glass card background
  const glassBg = isDark
    ? 'rgba(28,24,22,0.72)'
    : 'rgba(255,255,255,0.82)';
  const glassBorder = isDark
    ? '1px solid rgba(255,255,255,0.07)'
    : '1px solid rgba(255,255,255,0.9)';

  return createTheme({
    ...getDesignTokens(mode),
    typography: {
      fontFamily: typography.fontFamily,
      h1: typography.h1, h2: typography.h2, h3: typography.h3,
      h4: typography.h4, h5: typography.h5, h6: typography.h6,
      body1: typography.body1, body2: typography.body2,
      button: typography.button, caption: typography.caption,
      overline: typography.overline,
    },
    shape: { borderRadius: borderRadius.md },

    components: {
      // ── Global baseline ──────────────────────────────────────────────────
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: isDark ? food.darkBg : food.bg,
            backgroundImage: isDark
              ? `radial-gradient(ellipse 70% 55% at 0% 0%, rgba(224,42,42,0.07) 0%, transparent 70%),
                 radial-gradient(ellipse 70% 55% at 100% 100%, rgba(56,142,60,0.05) 0%, transparent 70%)`
              : `radial-gradient(ellipse 70% 55% at 0% 0%, ${food.bgPeach} 0%, transparent 70%),
                 radial-gradient(ellipse 70% 55% at 100% 100%, ${food.bgMint} 0%, transparent 70%)`,
            backgroundAttachment: 'scroll',
            scrollbarWidth: 'thin',
            scrollbarColor: `${isDark ? '#3A2E28' : '#D4C4B8'} transparent`,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? '#3A2E28' : '#D4C4B8',
              borderRadius: '3px',
            },
          },
        },
      },

      // ── Card — glass morphism ─────────────────────────────────────────────
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.xl,
            background: glassBg,
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: glassBorder,
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)'
              : '0 8px 32px rgba(26,18,9,0.08), 0 2px 8px rgba(26,18,9,0.04)',
            backgroundImage: 'none',
            transition: 'transform 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275), background-color 0.15s ease',
            '&:hover': {
              boxShadow: isDark
                ? '0 16px 48px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.35)'
                : '0 16px 48px rgba(26,18,9,0.12), 0 4px 16px rgba(26,18,9,0.06)',
              transform: 'translateY(-4px)',
            },
            '&:active': {
              transform: 'translateY(2px) scale(0.98)',
              boxShadow: isDark
                ? '0 4px 16px rgba(0,0,0,0.3)'
                : '0 4px 16px rgba(26,18,9,0.06)',
            },
          },
        },
      },

      // ── Paper — glass morphism ────────────────────────────────────────────
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: borderRadius.xl,
            background: glassBg,
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: glassBorder,
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.45)'
              : '0 8px 32px rgba(26,18,9,0.08)',
          },
          elevation1: {
            boxShadow: isDark
              ? '0 4px 16px rgba(0,0,0,0.4)'
              : '0 4px 16px rgba(26,18,9,0.06)',
          },
          elevation2: {
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.45)'
              : '0 8px 32px rgba(26,18,9,0.08)',
          },
          elevation3: {
            boxShadow: isDark
              ? '0 16px 48px rgba(0,0,0,0.55)'
              : '0 16px 48px rgba(26,18,9,0.12)',
          },
        },
      },

      // ── Button — pill shape with red accent ───────────────────────────────
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.full,
            padding: '10px 28px',
            fontWeight: 700,
            textTransform: 'none' as const,
            transition: 'transform 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275), background-color 0.15s ease',
            letterSpacing: '-0.01em',
            position: 'relative',
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
            boxShadow: isDark 
              ? '0 4px 0 rgba(0,0,0,0.35), 0 4px 12px rgba(0,0,0,0.2)'
              : `0 4px 0 ${food.redGlow}, 0 4px 12px rgba(224,42,42,0.15)`,
            color: '#fff',
            '&:hover': {
              background: `linear-gradient(135deg, ${food.redDark} 0%, ${food.red} 100%)`,
              boxShadow: isDark
                ? '0 6px 0 rgba(0,0,0,0.35), 0 8px 18px rgba(0,0,0,0.25)'
                : `0 6px 0 ${food.redGlow}, 0 8px 18px rgba(224,42,42,0.2)`,
              transform: 'translateY(-3px)',
            },
            '&:active': {
              boxShadow: isDark
                ? '0 0px 0 rgba(0,0,0,0.35), 0 2px 4px rgba(0,0,0,0.15)'
                : `0 0px 0 ${food.redGlow}, 0 2px 4px rgba(224,42,42,0.1)`,
              transform: 'translateY(3px) scale(0.97)',
            },
          },
          containedSecondary: {
            background: 'linear-gradient(135deg, #388E3C 0%, #66BB6A 100%)',
            boxShadow: '0 4px 0 rgba(56,142,60,0.25), 0 4px 12px rgba(56,142,60,0.15)',
            color: '#fff',
            '&:hover': {
              background: 'linear-gradient(135deg, #2E7D32 0%, #388E3C 100%)',
              boxShadow: '0 6px 0 rgba(56,142,60,0.25), 0 8px 18px rgba(56,142,60,0.2)',
              transform: 'translateY(-3px)',
            },
            '&:active': { 
              boxShadow: '0 0px 0 rgba(56,142,60,0.25), 0 2px 4px rgba(56,142,60,0.1)', 
              transform: 'translateY(3px) scale(0.97)' 
            },
          },
          outlined: {
            borderRadius: borderRadius.full,
            borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(200,185,170,0.5)',
            background: glassBg,
            backdropFilter: 'blur(12px)',
            boxShadow: isDark 
              ? '0 2px 0 rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.1)'
              : '0 2px 0 rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.05)',
            '&:hover': {
              borderColor: food.red,
              background: food.redSoft,
              boxShadow: `0 4px 0 ${food.redGlow}, 0 4px 12px rgba(224,42,42,0.1)`,
              transform: 'translateY(-2px)',
            },
            '&:active': { 
              boxShadow: '0 0px 0 rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.05)',
              transform: 'translateY(2px) scale(0.97)' 
            },
          },
          text: {
            borderRadius: borderRadius.full,
            '&:hover': {
              background: isDark ? 'rgba(255,255,255,0.06)' : food.redSoft,
              transform: 'translateY(-1px)',
            },
            '&:active': { 
              transform: 'translateY(1px) scale(0.98)' 
            },
          },
        },
      },

      // ── IconButton ────────────────────────────────────────────────────────
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.md,
            background: glassBg,
            backdropFilter: 'blur(12px)',
            border: glassBorder,
            boxShadow: isDark
              ? '0 3px 0 rgba(0,0,0,0.3), 0 2px 6px rgba(0,0,0,0.2)'
              : '0 3px 0 rgba(0,0,0,0.06), 0 2px 6px rgba(26,18,9,0.04)',
            transition: 'transform 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275), background-color 0.15s ease',
            '&:hover': {
              boxShadow: isDark
                ? '0 5px 0 rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.25)'
                : '0 5px 0 rgba(0,0,0,0.06), 0 4px 12px rgba(26,18,9,0.08)',
              transform: 'translateY(-2px)',
              background: glassBg,
            },
            '&:active': {
              transform: 'translateY(2px) scale(0.96)',
              boxShadow: `0 0px 0 ${food.redGlow}, 0 1px 3px rgba(0,0,0,0.1)`,
            },
          },
        },
      },

      // ── TextField ─────────────────────────────────────────────────────────
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: borderRadius.lg,
              background: glassBg,
              backdropFilter: 'blur(12px)',
              transition: 'all 0.2s ease',
              '& fieldset': {
                borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(200,185,170,0.4)',
                transition: 'border-color 0.2s ease',
              },
              '&:hover fieldset': {
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(200,185,170,0.7)',
              },
              '&.Mui-focused fieldset': {
                borderColor: food.red,
                borderWidth: '1.5px',
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${food.redSoft}`,
              },
            },
            '& .MuiInputLabel-root': {
              fontWeight: 500,
              color: n.textSecondary,
              '&.Mui-focused': { color: food.red },
            },
            '& .MuiOutlinedInput-input': { color: n.textPrimary },
          },
        },
      },

      // ── Select ────────────────────────────────────────────────────────────
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.lg,
            background: glassBg,
            backdropFilter: 'blur(12px)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(200,185,170,0.4)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(200,185,170,0.7)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: food.red,
            },
          },
        },
      },

      // ── Chip ─────────────────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.full,
            fontWeight: 600,
            backdropFilter: 'blur(12px)',
            boxShadow: isDark
              ? '0 2px 8px rgba(0,0,0,0.25)'
              : '0 2px 8px rgba(26,18,9,0.05)',
            transition: 'transform 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275), background-color 0.15s ease',
            '&.MuiChip-clickable:hover': {
              transform: 'translateY(-2px)',
              boxShadow: isDark
                ? '0 4px 12px rgba(0,0,0,0.35)'
                : '0 4px 12px rgba(26,18,9,0.10)',
            },
            '&.MuiChip-clickable:active': {
              transform: 'translateY(2px) scale(0.96)',
              boxShadow: isDark
                ? '0 1px 4px rgba(0,0,0,0.2)'
                : '0 1px 4px rgba(26,18,9,0.05)',
            },
          },
          colorDefault: {
            background: glassBg,
            border: glassBorder,
          },
          outlined: {
            background: glassBg,
            border: glassBorder,
          },
        },
      },

      // ── Dialog ────────────────────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: borderRadius.xl,
            background: isDark ? 'rgba(28,24,22,0.92)' : 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(32px)',
            WebkitBackdropFilter: 'blur(32px)',
            border: glassBorder,
            boxShadow: isDark
              ? '0 24px 64px rgba(0,0,0,0.6)'
              : '0 24px 64px rgba(26,18,9,0.15)',
          },
        },
      },

      // ── Tooltip ───────────────────────────────────────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: borderRadius.sm,
            background: isDark ? 'rgba(28,24,22,0.92)' : food.inkDark,
            fontSize: '0.8rem',
            padding: '6px 12px',
            boxShadow: isDark
              ? '0 4px 16px rgba(0,0,0,0.5)'
              : '0 4px 16px rgba(26,18,9,0.15)',
          },
        },
      },

      // ── ListItemButton ────────────────────────────────────────────────────
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.lg,
            margin: '2px 8px',
            padding: '10px 14px',
            transition: 'transform 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275), background-color 0.15s ease',
            boxShadow: 'none',
            '&.Mui-selected': {
              background: isDark ? food.redSoft : food.redSoft,
              boxShadow: `0 2px 12px ${food.redGlow}`,
              '& .MuiListItemIcon-root': { color: food.red },
              '& .MuiListItemText-primary': { color: food.red, fontWeight: 700 },
            },
            '&:hover:not(.Mui-selected)': {
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)',
              boxShadow: isDark
                ? '0 2px 8px rgba(0,0,0,0.2)'
                : '0 2px 8px rgba(26,18,9,0.05)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(1px) scale(0.98)',
            },
          },
        },
      },

      // ── Table ─────────────────────────────────────────────────────────────
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(8px)',
              fontWeight: 700,
              color: n.textSecondary,
              fontSize: '0.78rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              border: 'none',
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableRow-root': {
              '&:hover': {
                '& .MuiTableCell-body': {
                  background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(224,42,42,0.03)',
                },
              },
            },
            '& .MuiTableCell-body': {
              border: 'none',
              borderBottom: `1px solid ${n.border}`,
            },
          },
        },
      },

      // ── Alert ─────────────────────────────────────────────────────────────
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.lg,
            backdropFilter: 'blur(12px)',
            boxShadow: isDark
              ? '0 4px 16px rgba(0,0,0,0.3)'
              : '0 4px 16px rgba(26,18,9,0.06)',
          },
        },
      },

      // ── Drawer ────────────────────────────────────────────────────────────
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: isDark
              ? 'rgba(24,21,14,0.95)'
              : 'rgba(250,247,243,0.95)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: 'none',
            borderRight: glassBorder,
            boxShadow: isDark
              ? '4px 0 24px rgba(0,0,0,0.5)'
              : '4px 0 24px rgba(26,18,9,0.08)',
          },
        },
      },

      // ── Divider ───────────────────────────────────────────────────────────
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: n.border,
            opacity: 0.8,
          },
        },
      },

      // ── Tabs ──────────────────────────────────────────────────────────────
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: borderRadius.full,
            transition: 'transform 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275), background-color 0.15s ease, color 0.15s ease',
            '&.Mui-selected': { color: food.red },
            '&:hover': {
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'translateY(2px) scale(0.96)',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: food.red,
            borderRadius: borderRadius.full,
          },
        },
      },

      // ── Snackbar ──────────────────────────────────────────────────────────
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.lg,
            backdropFilter: 'blur(16px)',
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.5)'
              : '0 8px 32px rgba(26,18,9,0.12)',
          },
        },
      },

      // ── Backdrop ──────────────────────────────────────────────────────────
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(6px)',
            background: isDark
              ? 'rgba(26,18,9,0.55)'
              : 'rgba(26,18,9,0.3)',
          },
        },
      },
    },
  });
};

export { colors, gradients, shadows, borderRadius, neu, brand, typography, displayFont };
export { food } from './palette';
export * from './animations';
